'use client';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { type LinkField } from 'lib/types';
import { cn } from 'lib/utils';
import {
  type BloomProps,
  Bloom,
  Button,
  type ButtonProps,
  Icon,
  RichText,
  Wrapper,
} from 'ui';

export type HeaderSectionProps = {
  className?: string;
  eyebrow?: string;
  eyebrowAsTitle?: boolean;
  title?: string;
  content?: string;
  buttonLink?: LinkField;
  buttonLinkVariant?: ButtonProps['variant'];
  alignment?: 'left' | 'center';
  disableMotion?: boolean;
  includeWrapper?: boolean;
  bloom?: BloomProps;
};

const HeaderSection = ({
  className,
  eyebrow,
  eyebrowAsTitle,
  title,
  content,
  buttonLink,
  buttonLinkVariant = 'secondary',
  alignment = 'center',
  disableMotion = false,
  includeWrapper = false,
  bloom,
}: HeaderSectionProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10%' });

  // Render nothing if no content is provided
  if (!eyebrow && !title && !content && !buttonLink?.text) {
    return null;
  }

  const hasContent = !!content || !!buttonLink?.text;
  const EyebrowTag = eyebrow && eyebrowAsTitle ? 'h2' : 'p';
  const TitleTag = eyebrow && eyebrowAsTitle ? 'p' : 'h2';

  // Animation props based on alignment
  const getAnimationProps = (delay = 0) => {
    if (disableMotion) return {};

    const isLeftAligned = alignment === 'left';
    return {
      initial: {
        opacity: 0,
        x: isLeftAligned ? -30 : 0,
        y: isLeftAligned ? 0 : 30,
      },
      animate: isInView
        ? {
            opacity: 1,
            x: 0,
            y: 0,
          }
        : {
            opacity: 0,
            x: isLeftAligned ? -30 : 0,
            y: isLeftAligned ? 0 : 30,
          },
      transition: {
        duration: 0.8,
        delay,
      },
    };
  };

  const MotionHeader = disableMotion ? 'header' : motion.header;
  const MotionDiv = disableMotion ? 'div' : motion.div;

  const headerContent = (
    <MotionHeader
      className={cn(
        'grid grid-cols-1 gap-6',
        {
          'text-center max-w-180 mx-auto': alignment === 'center',
          'lg:grid-cols-2 lg:gap-30': alignment !== 'center' && hasContent,
        },
        className
      )}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.3 }}
      {...{ ref }}
    >
      {/* Eyebrow & title container */}
      {(!!eyebrow || !!title) && (
        <MotionDiv
          className={cn('flex flex-col gap-4 lg:gap-6', {
            'h-full justify-center': alignment === 'center',
          })}
          {...getAnimationProps(0.1)}
        >
          {!!eyebrow && (
            <EyebrowTag
              className={cn('text-eyebrow font-mono uppercase', {
                'text-neutral-200': !!title,
              })}
            >
              {eyebrow}
            </EyebrowTag>
          )}

          {!!title && <TitleTag className="text-h2">{title}</TitleTag>}
        </MotionDiv>
      )}

      {/* Content & button link container */}
      {(!!content || !!buttonLink?.text) && (
        <MotionDiv
          className={cn('flex flex-col gap-10', {
            'h-full justify-center': alignment !== 'center',
          })}
          {...getAnimationProps(0.3)}
        >
          {!!content && (
            <RichText className="text-p1" theme="neutral" {...{ content }} />
          )}

          {!!buttonLink?.text && (
            <div>
              <Button
                href={buttonLink.href}
                target={buttonLink.target}
                variant={buttonLinkVariant}
                trackingProps={buttonLink.trackingProps}
              >
                {buttonLink.text}
                {buttonLink.target === '_blank' && (
                  <Icon
                    name="external-link-arrow"
                    className="w-3 h-auto ml-0.5 align-middle"
                    aria-label="(opens in a new tab)"
                  />
                )}
              </Button>
            </div>
          )}
        </MotionDiv>
      )}
    </MotionHeader>
  );

  return includeWrapper ? (
    <Wrapper as="section" variant="page-full" className="relative">
      {!!bloom && <Bloom {...bloom} />}
      <div className="relative z-2">{headerContent}</div>
    </Wrapper>
  ) : (
    headerContent
  );
};

export default HeaderSection;
